import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { listClients, listClientsFull, listEmployees } from "@/api/lookups";
import {
  addMeetingParticipant,
  removeMeetingParticipant,
  updateMeetingClient,
} from "@/api/meetings";
import type {
  ClientSummary,
  EmployeeSummary,
  MeetingCard,
  UUID,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCan } from "@/hooks/useCan";

export interface ParticipantsTabProps {
  card: MeetingCard;
}

/**
 * Participants tab.
 *
 * Two blocks share the tab:
 *
 *   1. «Клиент» — TM 8760a3e1 revisit. One client per meeting; the
 *      operator rebinds via a combobox dialog backed by ``GET /api/clients``
 *      that PATCHes ``/api/meetings/:id/client``.
 *   2. «Участники» — TM 9e46885b. Mirror the client-card UX: each row in
 *      the list ships a «×» button that POSTs to ``/participants/remove``,
 *      and a «+ Добавить сотрудника» button under the list opens a
 *      combobox dialog backed by ``GET /api/employees`` that POSTs to
 *      ``/participants/add``. The combobox filters out already-added
 *      employees so the operator cannot pick a duplicate (BE is also
 *      idempotent — this is purely UX).
 *
 * Both edit affordances are gated client-side by ``meeting_edit_participants``
 * (admin/manager any meeting, user only «свои»). The backend re-enforces
 * the same gate on the mutation, so this is hide-only — not a security
 * boundary.
 */
export function ParticipantsTab({ card }: ParticipantsTabProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const ownCtx = useMemo(
    () => ({ uploadedBy: card.uploaded_by, participants: card.participants }),
    [card.uploaded_by, card.participants],
  );
  const canEdit = useCan("meeting_edit_participants", ownCtx);

  const employees = useQuery({
    queryKey: ["employees-lookup"],
    // Inline-form lookup — explicit `null` keeps the global lookup
    // behaviour (no folder narrowing) so the meeting-card's
    // participants picker can still see employees from other folders.
    // Passing the function reference directly would forward
    // react-query's `QueryFunctionContext` as the first arg, which
    // since `listEmployees(folderId?: UUID | null)` would stringify
    // the context object into the URL.
    queryFn: () => listEmployees(null),
  });
  // The clients list query is the source of truth for the «Клиент:» line.
  // We don't ship a per-client lookup helper because the registry is
  // small (operators reported < 200 rows in v1) and the combobox uses
  // its own paged search anyway.
  const clients = useQuery({
    queryKey: ["clients-lookup"],
    queryFn: () => listClients(null),
  });

  const employeeNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of employees.data ?? []) m.set(e.id, e.full_name);
    return m;
  }, [employees.data]);
  const clientNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of clients.data ?? []) m.set(c.id, c.name);
    return m;
  }, [clients.data]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
  const clientMutation = useMutation({
    mutationFn: (clientId: UUID) => updateMeetingClient(card.id, clientId),
    onSuccess: () => {
      // Invalidate every query that surfaces the client field for this
      // meeting: the card itself, the matching snapshot (its client tile
      // re-renders from the new bound_entity_id), and the registry list
      // (the «Клиент» column on the dashboard). Each invalidate is
      // cheap; the alternative — surgical setQueryData merges — is too
      // brittle for the gain.
      void queryClient.invalidateQueries({ queryKey: ["meeting", card.id] });
      void queryClient.invalidateQueries({ queryKey: ["matching", card.id] });
      void queryClient.invalidateQueries({ queryKey: ["meetings"] });
      setPickerOpen(false);
    },
  });

  // TM 9e46885b — separate mutation per direction so the «×» row buttons
  // can each surface their own error state without coupling to the add
  // dialog. Both invalidate the same set of queries because the
  // ``participants[]`` change ripples into the matching tab badges
  // (mention-but-not-in-participants chips).
  const addMutation = useMutation({
    mutationFn: (employeeId: UUID) =>
      addMeetingParticipant(card.id, employeeId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["meeting", card.id] });
      void queryClient.invalidateQueries({ queryKey: ["matching", card.id] });
      void queryClient.invalidateQueries({
        queryKey: ["meeting-matching", card.id],
      });
      setEmployeePickerOpen(false);
    },
  });
  const removeMutation = useMutation({
    mutationFn: (employeeId: UUID) =>
      removeMeetingParticipant(card.id, employeeId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["meeting", card.id] });
      void queryClient.invalidateQueries({ queryKey: ["matching", card.id] });
      void queryClient.invalidateQueries({
        queryKey: ["meeting-matching", card.id],
      });
    },
  });

  function handleRemoveClick(employeeId: UUID, displayName: string): void {
    // Synchronous `window.confirm` is the v1 confirmation pattern Dmitri
    // approved (mirrors the client-card delete affordance). A Dialog-based
    // confirm would force two clicks to undo a no-op; the confirm box is
    // fine for «zeркало того что есть с клиентом».
    const message = t("meetings.card.participantsRemove.confirm", {
      name: displayName,
    });
    if (typeof window !== "undefined" && !window.confirm(message)) return;
    removeMutation.mutate(employeeId);
  }

  return (
    <div className="space-y-6" data-testid="participants-tab">
      <section data-testid="meeting-client-block">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t("meetings.card.clientBlock.title")}
        </h3>
        <div className="mt-1 flex items-center gap-2">
          {card.client_id !== null ? (
            <Link
              to="/clients/$clientId"
              params={{ clientId: card.client_id }}
              className="text-sm text-primary underline-offset-4 hover:underline"
              data-testid="meeting-client-link"
            >
              {clientNameById.get(card.client_id) ??
                (clients.isLoading ? "…" : card.client_id)}
            </Link>
          ) : (
            <span className="text-sm">
              {t("meetings.card.clientBlock.empty")}
            </span>
          )}
          {canEdit ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setPickerOpen(true)}
              data-testid="meeting-change-client"
            >
              {t("meetings.card.clientBlock.changeButton")}
            </Button>
          ) : null}
        </div>
        {clientMutation.isError ? (
          <p
            role="alert"
            className="mt-1 text-xs text-destructive"
            data-testid="meeting-client-error"
          >
            {t("meetings.card.clientBlock.saveError")}
          </p>
        ) : null}
      </section>

      <section data-testid="meeting-participants-block">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t("meetings.card.tabs.participants")}
        </h3>
        {card.participants.length === 0 ? (
          <p className="mt-1 text-sm text-muted-foreground">
            {t("meetings.card.participantsEmpty")}
          </p>
        ) : (
          <ul
            className="mt-1 space-y-1 text-sm"
            data-testid="meeting-participants-list"
          >
            {card.participants.map((id) => {
              const displayName =
                employeeNameById.get(id) ?? (employees.isLoading ? "…" : id);
              return (
                <li
                  key={id}
                  className="flex items-center gap-2"
                  data-testid={`meeting-participant-row-${id}`}
                >
                  <Link
                    to="/employees/$employeeId"
                    params={{ employeeId: id }}
                    className="text-primary underline-offset-4 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`meeting-participant-link-${id}`}
                  >
                    {displayName}
                  </Link>
                  {canEdit ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveClick(id, displayName)}
                      disabled={removeMutation.isPending}
                      aria-label={t("meetings.card.participantsRemove.aria", {
                        name: displayName,
                      })}
                      data-testid={`meeting-participant-remove-${id}`}
                    >
                      ×
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
        {canEdit ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => setEmployeePickerOpen(true)}
            data-testid="meeting-add-participant"
          >
            {t("meetings.card.participantsAdd.button")}
          </Button>
        ) : null}
        {addMutation.isError || removeMutation.isError ? (
          <p
            role="alert"
            className="mt-1 text-xs text-destructive"
            data-testid="meeting-participants-error"
          >
            {t("meetings.card.participantsAdd.saveError")}
          </p>
        ) : null}
      </section>

      {pickerOpen ? (
        <ClientPickerDialog
          currentClientId={card.client_id}
          onClose={() => setPickerOpen(false)}
          onSubmit={(clientId) => clientMutation.mutate(clientId)}
          busy={clientMutation.isPending}
        />
      ) : null}

      {employeePickerOpen ? (
        <EmployeePickerDialog
          excludeIds={card.participants}
          folderId={card.folder_id ?? null}
          onClose={() => setEmployeePickerOpen(false)}
          onSubmit={(employeeId) => addMutation.mutate(employeeId)}
          busy={addMutation.isPending}
        />
      ) : null}
    </div>
  );
}

interface ClientPickerDialogProps {
  currentClientId: UUID | null;
  onClose: () => void;
  onSubmit: (clientId: UUID) => void;
  busy: boolean;
}

/**
 * Combobox-backed picker for «Изменить клиента».
 *
 * Separate from `ClientFormDialog` (which is the create/edit form for
 * the registry itself) on purpose — this dialog only *selects* an
 * existing client. Reuses the same `Combobox` primitive the upload
 * dialog uses so the operator's mental model is consistent across the
 * app. Submit is disabled until the operator picks a row different
 * from the current binding so we don't fire the PATCH on a no-op.
 */
function ClientPickerDialog({
  currentClientId,
  onClose,
  onSubmit,
  busy,
}: ClientPickerDialogProps) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<ComboboxOption<ClientSummary> | null>(
    null,
  );

  async function searchClients(
    q: string,
  ): Promise<ComboboxOption<ClientSummary>[]> {
    const rows = await listClientsFull({ search: q, limit: 20 });
    return rows.map((r) => ({
      value: r.id,
      data: { id: r.id, name: r.name, aliases: r.aliases },
      label: r.name,
      secondary:
        r.aliases && r.aliases.length > 0 ? r.aliases.join(", ") : undefined,
    }));
  }

  function handleSubmit(): void {
    if (picked === null) return;
    if (picked.value === currentClientId) {
      // No-op short-circuit: closing without firing the PATCH keeps
      // react-query happy and the audit log clean.
      onClose();
      return;
    }
    onSubmit(picked.value);
  }

  const submitDisabled =
    busy || picked === null || picked.value === currentClientId;

  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v && !busy) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("meetings.card.clientPicker.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3" data-testid="meeting-client-picker">
          <Combobox<ClientSummary>
            id="meeting-client-picker-combobox"
            mode="single"
            value={picked}
            onChange={setPicked}
            search={searchClients}
            placeholder={t("meetings.card.clientPicker.searchPlaceholder")}
            emptyLabel={t("meetings.card.clientPicker.emptyLabel")}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("meetings.card.clientPicker.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitDisabled}
              data-testid="meeting-client-picker-submit"
            >
              {t("meetings.card.clientPicker.submit")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface EmployeePickerDialogProps {
  /** Employee ids already in `meeting.participants[]` — filtered out of the dropdown. */
  excludeIds: readonly UUID[];
  /** Folder narrow for the employee registry (papki-tenants v1). */
  folderId: UUID | null;
  onClose: () => void;
  onSubmit: (employeeId: UUID) => void;
  busy: boolean;
}

/**
 * Combobox-backed picker for «+ Добавить сотрудника».
 *
 * Mirror of :func:`ClientPickerDialog` for the participants list. We
 * use the simpler `listEmployees` (summary DTOs) rather than the full
 * registry call because the picker only needs id + full_name to render
 * options; if a future revision needs custom-field metadata we can swap
 * to `listEmployeesFull` without changing the JSX.
 *
 * The `excludeIds` prop filters out employees already in the meeting
 * so the dropdown can't surface a duplicate. The backend is also
 * idempotent — this is purely UX so the operator doesn't see a no-op
 * pick.
 */
function EmployeePickerDialog({
  excludeIds,
  folderId,
  onClose,
  onSubmit,
  busy,
}: EmployeePickerDialogProps) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<ComboboxOption<EmployeeSummary> | null>(
    null,
  );

  const excludedSet = useMemo(() => new Set(excludeIds), [excludeIds]);

  async function searchEmployees(
    q: string,
  ): Promise<ComboboxOption<EmployeeSummary>[]> {
    const rows = await listEmployees(folderId);
    const query = q.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (excludedSet.has(r.id)) return false;
      if (query === "") return true;
      const hay = [r.full_name, ...(r.aliases ?? [])].join(" ").toLowerCase();
      return hay.includes(query);
    });
    return filtered.slice(0, 20).map((r) => ({
      value: r.id,
      data: { id: r.id, full_name: r.full_name, aliases: r.aliases },
      label: r.full_name,
      secondary:
        r.aliases && r.aliases.length > 0 ? r.aliases.join(", ") : undefined,
    }));
  }

  function handleSubmit(): void {
    if (picked === null) return;
    onSubmit(picked.value);
  }

  const submitDisabled = busy || picked === null;

  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v && !busy) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("meetings.card.participantsAdd.dialogTitle")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3" data-testid="meeting-employee-picker">
          <Combobox<EmployeeSummary>
            id="meeting-employee-picker-combobox"
            mode="single"
            value={picked}
            onChange={setPicked}
            search={searchEmployees}
            placeholder={t("meetings.card.participantsAdd.searchPlaceholder")}
            emptyLabel={t("meetings.card.participantsAdd.emptyLabel")}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("meetings.card.participantsAdd.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitDisabled}
              data-testid="meeting-employee-picker-submit"
            >
              {t("meetings.card.participantsAdd.submit")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
